// -----------------------------------------------------------------------
// <copyright file="InAppReader.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { IStackStyles, Stack } from '@fluentui/react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState, UITabView, UITabViewInfo, UITabViewMode } from '../../../web-legacy/models/reduxState';
import { updateUITabViewInfo } from '../../../web-legacy/packages/app/appActions';
import { FullSpinner } from '../../common/components/FullSpinner';
import { getHelpLogger } from '../../utils/getHelpLogger';

const iframeRootStackStyles: IStackStyles = {
  root: {
    height: '100%',
  },
};

const getChromelessUrl = (url: string): string => {
  if (url && !url.includes('javascript')) {
    return url.includes('?') ? url.concat('&nochrome') : url.concat('?nochrome');
  }
  return '';
};

const previousUiTabViewStringSelector = (state: IAppState): UITabView => {
  const stack = state.NavState.uITabViewStack;
  if (stack && stack.length > 1) {
    return stack[stack.length - 1].uITabView;
  } else {
    return null;
  }
};

let pageLoadCount = 0;
export const InAppReader = (): JSX.Element => {
  const [isArticleLoading, setLoadingStatus] = useState(true);
  const url = useSelector((state: IAppState) => state.NavState.contactIframeSrc);
  const origin = useSelector(previousUiTabViewStringSelector);
  const dispatch = useDispatch();

  const onLoad = () => {
    setLoadingStatus(false);
    const uiTabViewInfo: UITabViewInfo = {
      viewMode: UITabViewMode.readOnly,
      uITabView: UITabView.articleReader,
      showAppBarBackButton: true,
      url: url,
      requestId: null,
    };
    dispatch(updateUITabViewInfo(uiTabViewInfo));
    pageLoadCount += 1;
    getHelpLogger.logArticleLoadingFinished(url, origin, pageLoadCount);
  };

  const onError = () => {
    setLoadingStatus(false);
    getHelpLogger.logArticleLoadError(url, origin);
  };

  useEffect(() => {
    getHelpLogger.logArticleRequested(url, origin);
    pageLoadCount = 0;
  }, []);

  return (
    <Stack styles={iframeRootStackStyles}>
      <iframe
        src={getChromelessUrl(url)}
        height="100%"
        width="100%"
        style={{ border: 'none' }}
        title={url}
        onLoad={onLoad}
        onError={onError}
        sandbox="allow-scripts allow-popups"
      />
      {isArticleLoading ? <FullSpinner /> : null}
    </Stack>
  );
};
